import React from 'react';
import PropTypes from "prop-types";
export var EuiSelectableOptionProps = function EuiSelectableOptionProps() {
  return ___EmotionJSX("div", null);
};
EuiSelectableOptionProps.propTypes = {
  /**
     * Optional `boolean`.
     * Set to `true` to indicate object is just a grouping label, not a selectable item
     */
  isGroupLabel: PropTypes.oneOfType([PropTypes.oneOf([true]).isRequired, PropTypes.oneOf([false])]),
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,

  /**
     * Visible label of option.
     * Must be unique across items if `key` is not supplied
     */
  label: PropTypes.string,

  /**
     * Optionally change the searchable term by passing a different string other than the `label`.
     * Best used when creating a custom `optionRender` to separate the label from metadata but allowing to search on both
     */
  searchableLabel: PropTypes.string,

  /**
     * Must be unique across items.
     * Will be used to match options instead of `label`
     */
  key: PropTypes.string,

  /**
     * Leave `undefined` to indicate not selected,
     * 'on' to indicate inclusion and
     * 'off' to indicate exclusion
     */
  checked: PropTypes.oneOf(["on", "off", undefined]),
  disabled: PropTypes.bool,

  /**
     * Node to add between the selection icon and the label
     */
  prepend: PropTypes.node,

  /**
     * Node to add to the far right of the item
     */
  append: PropTypes.node,
  ref: PropTypes.func,

  /**
     * Option data to pass through to the `renderOptions` element.
     * Bypass `EuiSelectableItem` and avoid DOM attribute warnings.
     */
  data: PropTypes.shape({})
};
export var EuiSelectableOptionsList = function EuiSelectableOptionsList() {
  return ___EmotionJSX("div", null);
};
EuiSelectableOptionsList.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,

  /**
       * The index of the option to be highlighted as pseudo-focused;
       * Good for use when only one selection is allowed and needing to open
       * directly to that option
       */
  activeOptionIndex: PropTypes.number,

  /**
       * Show the check/cross selection indicator icons
       */
  showIcons: PropTypes.bool,
  singleSelection: PropTypes.oneOfType([PropTypes.oneOf(["always"]), PropTypes.bool.isRequired]),

  /**
       * Any props to send specifically to the react-window `FixedSizeList`
       */
  windowProps: PropTypes.any,

  /**
       * Adds a border around the list to indicate the bounds;
       * Useful when the list scrolls, otherwise use your own container
       */
  bordered: PropTypes.bool,

  /**
       * When enabled by setting to either `true` or passing custom text,
       * shows a hollow badge as an append (far right) when the item is focused.
       * The default content when `true` is `↩ to select/deselect/include/exclude`
       */
  onFocusBadge: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.shape({
    /**
       * Accepts any string from our icon library
       */
    iconType: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addDataApp", "advancedSettingsApp", "agentApp", "aggregate", "alert", "analyzeEvent", "annotation", "apmApp", "apmTrace", "appSearchApp", "apps", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "arrowStart", "arrowEnd", "asterisk", "auditbeatApp", "beaker", "bell", "bellSlash", "bolt", "boxesHorizontal", "boxesVertical", "branch", "branchUser", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "casesApp", "check", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "codeApp", "color", "compute", "console", "consoleApp", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crossInACircleFilled", "crosshairs", "currency", "cut", "dashboardApp", "dataVisualizer", "database", "desktop", "devToolsApp", "discoverApp", "document", "documentEdit", "documentation", "documents", "dot", "doubleArrowLeft", "doubleArrowRight", "download", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorChecklist", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignLeft", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "eql", "eraser", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "flag", "fleetApp", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "gear", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "inputOutput", "inspect", "invert", "ip", "keyboardShortcut", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "layers", "lensApp", "lettering", "lineDashed", "lineDotted", "lineSolid", "link", "list", "listAdd", "lock", "lockOpen", "logoAWS", "logoAWSMono", "logoAerospike", "logoApache", "logoAppSearch", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticStack", "logoElasticsearch", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logsApp", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithExclamation", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "paperClip", "partial", "pause", "payment", "pencil", "percent", "pin", "pinFilled", "pipelineApp", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "popout", "push", "questionInCircle", "quote", "recentlyViewedApp", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "sessionViewer", "shard", "share", "snowflake", "sortDown", "sortLeft", "sortRight", "sortUp", "sortable", "spacesApp", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "sun", "swatchInput", "symlink", "tableDensityCompact", "tableDensityExpanded", "tableDensityNormal", "tableOfContents", "tag", "tear", "temperature", "timeline", "timelionApp", "timeRefresh", "timeslider", "training", "trash", "unfold", "unlink", "upgradeAssistantApp", "uptimeApp", "user", "userAvatar", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visTable", "visTagCloud", "visText", "visTimelion", "visVega", "visVisualBuilder", "visualizeApp", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenClass", "tokenProperty", "tokenEnum", "tokenVariable", "tokenMethod", "tokenAnnotation", "tokenException", "tokenInterface", "tokenParameter", "tokenField", "tokenElement", "tokenFunction", "tokenBoolean", "tokenString", "tokenArray", "tokenNumber", "tokenConstant", "tokenObject", "tokenEvent", "tokenKey", "tokenNull", "tokenStruct", "tokenPackage", "tokenOperator", "tokenEnumMember", "tokenRepo", "tokenSymbol", "tokenFile", "tokenModule", "tokenNamespace", "tokenDate", "tokenIP", "tokenNested", "tokenAlias", "tokenShape", "tokenGeo", "tokenRange", "tokenBinary", "tokenJoin", "tokenPercolator", "tokenFlattened", "tokenRankFeature", "tokenRankFeatures", "tokenKeyword", "tokenTag", "tokenCompletionSuggester", "tokenDenseVector", "tokenText", "tokenTokenCount", "tokenSearchType", "tokenHistogram"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]),

    /**
       * The side of the badge the icon should sit
       */
    iconSide: PropTypes.oneOf(["left", "right"]),

    /**
       * Accepts either our palette colors (primary, success ..etc) or a hex value `#FFFFFF`, `#000`.
       */
    color: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.oneOf(["default", "primary", "success", "accent", "warning", "danger", "text", "subdued", "ghost", "inherit"]).isRequired]),

    /**
       * Will override any color passed through the `color` prop.
       */
    isDisabled: PropTypes.bool,

    /**
       * Props passed to the close button.
       */
    closeButtonProps: PropTypes.any,
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,

    /**
       * Will apply an onclick to icon within the badge
       */
    iconOnClick: PropTypes.func,

    /**
       * Aria label applied to the iconOnClick button
       */
    iconOnClickAriaLabel: PropTypes.any,

    /**
       * Will apply an onclick to the badge itself
       */
    onClick: PropTypes.func,

    /**
       * Aria label applied to the onClick button
       */
    onClickAriaLabel: PropTypes.any,
    href: PropTypes.string,
    target: PropTypes.string,
    rel: PropTypes.string
  }).isRequired]),

  /**
       * Padding for the list items.
       */
  paddingSize: PropTypes.any,

  /**
       * How to handle long text within the item.
       * Wrapping only works if virtualization is off.
       */
  textWrap: PropTypes.oneOf(["truncate", "wrap"]),

  /**
       * Use virtualized rendering for list items with `react-window`.
       * Sets each row's height to the value of `rowHeight`.
       */
  isVirtualized: PropTypes.oneOfType([PropTypes.oneOf([true]), PropTypes.oneOf([false]).isRequired]),

  /**
       *  The height of each option in pixels. Defaults to `32`.
       *  Has no effect if `isVirtualized=false`.
       */
  rowHeight: PropTypes.number
};
export var EuiSelectableSearchProps = function EuiSelectableSearchProps() {
  return ___EmotionJSX("div", null);
};
export var Options = function Options() {
  return ___EmotionJSX("div", null);
};
Options.propTypes = {
  /**
     * Displayed on the left (`prepend`).
     * Object of `EuiIconProps` for display of the solution/application's logo
     */
  icon: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,

    /**
         * Descriptive title for naming the icon based on its use
         */
    title: PropTypes.string,

    /**
         * A unique identifier for the title element
         */
    titleId: PropTypes.string,

    /**
         * Its value should be one or more element IDs
         */
    "aria-labelledby": PropTypes.string,

    /**
         * Callback when the icon has been loaded & rendered
         */
    onIconLoad: PropTypes.func
  }),

  /**
     * Displayed on the right (`append`).
     * Object of `EuiAvatarProps` for display of the space (default) or user
     */
  avatar: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,

    /**
           * Custom initials (max 2 characters).
           * By default will take the first character (of each word).
           */
    initials: PropTypes.string,

    /**
           * Specify how many characters to show (1 or 2).
           * By default, will show based on number of words (max first 2).
           */
    initialsLength: PropTypes.oneOf([1, 2]),

    /**
           * Path to an image to display instead of initials
           */
    imageUrl: PropTypes.string,

    /**
         * Any EUI glyph, logo or custom icon to display instead of initials
         */
    iconType: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addDataApp", "advancedSettingsApp", "agentApp", "aggregate", "alert", "analyzeEvent", "annotation", "apmApp", "apmTrace", "appSearchApp", "apps", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "arrowStart", "arrowEnd", "asterisk", "auditbeatApp", "beaker", "bell", "bellSlash", "bolt", "boxesHorizontal", "boxesVertical", "branch", "branchUser", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "casesApp", "check", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "codeApp", "color", "compute", "console", "consoleApp", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crossInACircleFilled", "crosshairs", "currency", "cut", "dashboardApp", "dataVisualizer", "database", "desktop", "devToolsApp", "discoverApp", "document", "documentEdit", "documentation", "documents", "dot", "doubleArrowLeft", "doubleArrowRight", "download", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorChecklist", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignLeft", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "eql", "eraser", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "flag", "fleetApp", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "gear", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "inputOutput", "inspect", "invert", "ip", "keyboardShortcut", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "layers", "lensApp", "lettering", "lineDashed", "lineDotted", "lineSolid", "link", "list", "listAdd", "lock", "lockOpen", "logoAWS", "logoAWSMono", "logoAerospike", "logoApache", "logoAppSearch", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticStack", "logoElasticsearch", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logsApp", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithExclamation", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "paperClip", "partial", "pause", "payment", "pencil", "percent", "pin", "pinFilled", "pipelineApp", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "popout", "push", "questionInCircle", "quote", "recentlyViewedApp", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "sessionViewer", "shard", "share", "snowflake", "sortDown", "sortLeft", "sortRight", "sortUp", "sortable", "spacesApp", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "sun", "swatchInput", "symlink", "tableDensityCompact", "tableDensityExpanded", "tableDensityNormal", "tableOfContents", "tag", "tear", "temperature", "timeline", "timelionApp", "timeRefresh", "timeslider", "training", "trash", "unfold", "unlink", "upgradeAssistantApp", "uptimeApp", "user", "userAvatar", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visTable", "visTagCloud", "visText", "visTimelion", "visVega", "visVisualBuilder", "visualizeApp", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenClass", "tokenProperty", "tokenEnum", "tokenVariable", "tokenMethod", "tokenAnnotation", "tokenException", "tokenInterface", "tokenParameter", "tokenField", "tokenElement", "tokenFunction", "tokenBoolean", "tokenString", "tokenArray", "tokenNumber", "tokenConstant", "tokenObject", "tokenEvent", "tokenKey", "tokenNull", "tokenStruct", "tokenPackage", "tokenOperator", "tokenEnumMember", "tokenRepo", "tokenSymbol", "tokenFile", "tokenModule", "tokenNamespace", "tokenDate", "tokenIP", "tokenNested", "tokenAlias", "tokenShape", "tokenGeo", "tokenRange", "tokenBinary", "tokenJoin", "tokenPercolator", "tokenFlattened", "tokenRankFeature", "tokenRankFeatures", "tokenKeyword", "tokenTag", "tokenCompletionSuggester", "tokenDenseVector", "tokenText", "tokenTokenCount", "tokenSearchType", "tokenHistogram"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]),

    /**
         * Manually change icon size
         */
    iconSize: PropTypes.oneOf(["original", "s", "m", "l", "xl", "xxl"]),

    /**
         * Manually change icon color
         */
    iconColor: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.oneOf(["default", "primary", "success", "accent", "warning", "danger", "text", "subdued", "ghost", "inherit"]).isRequired]).isRequired, PropTypes.oneOf([null])]),

    /**
         * Full name of avatar for title attribute and calculating initial if not provided
         */
    name: PropTypes.string.isRequired,

    /**
         * Accepts hex values like `#FFFFFF`, `#000` otherwise a viz palette color will be assigned.
         * Or pass `'plain'` for an empty shade, `'subdued'` for a light gray shade or `null` to remove entirely and the text/icon color will `inherit`
         */
    color: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.oneOf(["plain", "subdued", null])]),

    /**
         * The type of avatar mainly controlling the shape.
         * `user` = circle
         * `space` = rounded square
         */
    type: PropTypes.any,
    size: PropTypes.any,

    /**
         * Grays out the avatar to simulate being disabled
         */
    isDisabled: PropTypes.bool
  }),

  /**
     * An array of inline #MetaData displayed beneath the label and separated by bullets.
     */
  meta: PropTypes.arrayOf(PropTypes.shape({
    /**
       * Required to display the metadata
       */
    text: PropTypes.string.isRequired,

    /**
       * Styles the metadata according to Elastic's schema.
       * Can be one of 'application', 'deployment', 'article', 'case', 'platform',
       * or a custom string to associate with your own schema.
       * Appends the string to the class name as `euiSelectableTemplateSitewide__optionMeta--[type]`
       */
    type: PropTypes.oneOfType([PropTypes.oneOf(["application", "deployment", "article", "case", "platform"]), PropTypes.string.isRequired]),

    /**
       * Will wrap the meta tag in EuiHighlight to mark the portions that match the search text
       */
    highlightSearchString: PropTypes.bool,
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string
  }).isRequired),

  /**
     * Optional `boolean`.
     * Set to `true` to indicate object is just a grouping label, not a selectable item
     */
  isGroupLabel: PropTypes.oneOfType([PropTypes.oneOf([true]).isRequired, PropTypes.oneOf([false])]),
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,

  /**
     * Visible label of option.
     * Must be unique across items if `key` is not supplied
     */
  label: PropTypes.string,

  /**
     * Optionally change the searchable term by passing a different string other than the `label`.
     * Best used when creating a custom `optionRender` to separate the label from metadata but allowing to search on both
     */
  searchableLabel: PropTypes.string,

  /**
     * Must be unique across items.
     * Will be used to match options instead of `label`
     */
  key: PropTypes.string,

  /**
     * Leave `undefined` to indicate not selected,
     * 'on' to indicate inclusion and
     * 'off' to indicate exclusion
     */
  checked: PropTypes.oneOf(["on", "off", undefined]),
  disabled: PropTypes.bool,

  /**
     * Node to add between the selection icon and the label
     */
  prepend: PropTypes.node,

  /**
     * Node to add to the far right of the item
     */
  append: PropTypes.node,
  ref: PropTypes.func,

  /**
     * Option data to pass through to the `renderOptions` element.
     * Bypass `EuiSelectableItem` and avoid DOM attribute warnings.
     */
  data: PropTypes.shape({})
};
export var MetaData = () => <div />;
MetaData.propTypes = {
  /**
     * Required to display the metadata
     */
  text: PropTypes.string.isRequired,

  /**
     * Styles the metadata according to Elastic's schema.
     * Can be one of 'application', 'deployment', 'article', 'case', 'platform',
     * or a custom string to associate with your own schema.
     * Appends the string to the class name as `euiSelectableTemplateSitewide__optionMeta--[type]`
     */
  type: PropTypes.oneOfType([PropTypes.oneOf(["application", "deployment", "article", "case", "platform"]), PropTypes.string.isRequired]),

  /**
     * Will wrap the meta tag in EuiHighlight to mark the portions that match the search text
     */
  highlightSearchString: PropTypes.bool,
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string
};

try {
  EuiSelectableOptionProps.__docgenInfo = {
    tags: {},
    filePath: '/app/src-docs/src/views/selectable/props.tsx',
    description: '',
    displayName: 'EuiSelectableOptionProps',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      label: {
        defaultValue: null,
        description: 'Visible label of option.\n' + 'Must be unique across items if `key` is not supplied',
        name: 'label',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      searchableLabel: {
        defaultValue: null,
        description: 'Optionally change the searchable term by passing a different string other than the `label`.\n' + 'Best used when creating a custom `optionRender` to separate the label from metadata but allowing to search on both',
        name: 'searchableLabel',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      key: {
        defaultValue: null,
        description: 'Must be unique across items.\n' + 'Will be used to match options instead of `label`',
        name: 'key',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      checked: {
        defaultValue: null,
        description: 'Leave `undefined` to indicate not selected,\n' + "'on' to indicate inclusion and\n" + "'off' to indicate exclusion",
        name: 'checked',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'EuiSelectableOptionCheckedType',
          value: [{
            value: '"off"'
          }, {
            value: '"on"'
          }]
        }
      },
      disabled: {
        defaultValue: null,
        description: '',
        name: 'disabled',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isGroupLabel: {
        defaultValue: null,
        description: 'Optional `boolean`.\n' + 'Set to `true` to indicate object is just a grouping label, not a selectable item',
        name: 'isGroupLabel',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      prepend: {
        defaultValue: null,
        description: 'Node to add between the selection icon and the label',
        name: 'prepend',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'any'
        }
      },
      append: {
        defaultValue: null,
        description: 'Node to add to the far right of the item',
        name: 'append',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'any'
        }
      },
      ref: {
        defaultValue: null,
        description: '',
        name: 'ref',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '(optionIndex: number) => void'
        }
      },
      id: {
        defaultValue: null,
        description: 'Disallow `id` from being set.\n' + 'Option item `id`s are coordinated at a higher level for a11y reasons.',
        name: 'id',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'never'
        }
      },
      data: {
        defaultValue: null,
        description: 'Option data to pass through to the `renderOptions` element.\n' + 'Bypass `EuiSelectableItem` and avoid DOM attribute warnings.',
        name: 'data',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '{ [key: string]: any; }'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'EuiFieldSearchProps', 'InputHTMLAttributes', 'EuiSelectableTemplateSitewideMetaData']
  };
} catch (e) {}

try {
  EuiSelectableOptionsList.__docgenInfo = {
    tags: {},
    filePath: '/app/src-docs/src/views/selectable/props.tsx',
    description: '',
    displayName: 'EuiSelectableOptionsList',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      activeOptionIndex: {
        defaultValue: null,
        description: 'The index of the option to be highlighted as pseudo-focused;\n' + 'Good for use when only one selection is allowed and needing to open\n' + 'directly to that option',
        name: 'activeOptionIndex',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_list/selectable_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      showIcons: {
        defaultValue: null,
        description: 'Show the check/cross selection indicator icons',
        name: 'showIcons',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_list/selectable_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      singleSelection: {
        defaultValue: null,
        description: '',
        name: 'singleSelection',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_list/selectable_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean | "always"'
        }
      },
      windowProps: {
        defaultValue: null,
        description: 'Any props to send specifically to the react-window `FixedSizeList`',
        name: 'windowProps',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_list/selectable_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'Partial<ListProps<any>>'
        }
      },
      bordered: {
        defaultValue: null,
        description: 'Adds a border around the list to indicate the bounds;\n' + 'Useful when the list scrolls, otherwise use your own container',
        name: 'bordered',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_list/selectable_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      onFocusBadge: {
        defaultValue: null,
        description: 'When enabled by setting to either `true` or passing custom text,\n' + 'shows a hollow badge as an append (far right) when the item is focused.\n' + 'The default content when `true` is `↩ to select/deselect/include/exclude`',
        name: 'onFocusBadge',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_list/selectable_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean | EuiBadgeProps'
        }
      },
      paddingSize: {
        defaultValue: null,
        description: 'Padding for the list items.',
        name: 'paddingSize',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_list/selectable_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "none"',
          value: [{
            value: '"s"'
          }, {
            value: '"none"'
          }]
        }
      },
      textWrap: {
        defaultValue: null,
        description: 'How to handle long text within the item.\n' + 'Wrapping only works if virtualization is off.',
        name: 'textWrap',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_list/selectable_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"truncate" | "wrap"',
          value: [{
            value: '"truncate"'
          }, {
            value: '"wrap"'
          }]
        }
      },
      rowHeight: {
        defaultValue: null,
        description: 'The height of each option in pixels. Defaults to `32`.\n' + 'Has no effect if `isVirtualized=false`.',
        name: 'rowHeight',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_list/selectable_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      isVirtualized: {
        defaultValue: null,
        description: 'Use virtualized rendering for list items with `react-window`.\n' + "Sets each row's height to the value of `rowHeight`.",
        name: 'isVirtualized',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_list/selectable_list.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/selectable/selectable_list/selectable_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'EuiFieldSearchProps', 'InputHTMLAttributes', 'EuiSelectableTemplateSitewideMetaData']
  };
} catch (e) {}

try {
  EuiSelectableSearchProps.__docgenInfo = {
    tags: {},
    filePath: '/app/src-docs/src/views/selectable/props.tsx',
    description: '',
    displayName: 'EuiSelectableSearchProps',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      id: {
        defaultValue: null,
        description: '',
        name: 'id',
        parent: {
          fileName: 'app/src/components/form/field_search/field_search.tsx',
          name: 'EuiFieldSearchProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/field_search/field_search.tsx',
          name: 'EuiFieldSearchProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      value: {
        defaultValue: null,
        description: '',
        name: 'value',
        parent: {
          fileName: 'app/src/components/form/field_search/field_search.tsx',
          name: 'EuiFieldSearchProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/field_search/field_search.tsx',
          name: 'EuiFieldSearchProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      append: {
        defaultValue: null,
        description: 'Creates an input group with element(s) coming after input.\n' + '`string` | `ReactElement` or an array of these',
        name: 'append',
        parent: {
          fileName: 'app/src/components/form/field_search/field_search.tsx',
          name: 'EuiFieldSearchProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/field_search/field_search.tsx',
          name: 'EuiFieldSearchProps'
        }],
        required: false,
        type: {
          name: 'PrependAppendType'
        }
      },
      prepend: {
        defaultValue: null,
        description: 'Creates an input group with element(s) coming before input\n' + '`string` | `ReactElement` or an array of these',
        name: 'prepend',
        parent: {
          fileName: 'app/src/components/form/field_search/field_search.tsx',
          name: 'EuiFieldSearchProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/field_search/field_search.tsx',
          name: 'EuiFieldSearchProps'
        }],
        required: false,
        type: {
          name: 'PrependAppendType'
        }
      },
      name: {
        defaultValue: null,
        description: '',
        name: 'name',
        parent: {
          fileName: 'app/src/components/form/field_search/field_search.tsx',
          name: 'EuiFieldSearchProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/field_search/field_search.tsx',
          name: 'EuiFieldSearchProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      fullWidth: {
        defaultValue: null,
        description: '',
        name: 'fullWidth',
        parent: {
          fileName: 'app/src/components/form/field_search/field_search.tsx',
          name: 'EuiFieldSearchProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/field_search/field_search.tsx',
          name: 'EuiFieldSearchProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isLoading: {
        defaultValue: null,
        description: '',
        name: 'isLoading',
        parent: {
          fileName: 'app/src/components/form/field_search/field_search.tsx',
          name: 'EuiFieldSearchProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/field_search/field_search.tsx',
          name: 'EuiFieldSearchProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      placeholder: {
        defaultValue: null,
        description: '',
        name: 'placeholder',
        parent: {
          fileName: 'app/src/components/form/field_search/field_search.tsx',
          name: 'EuiFieldSearchProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/field_search/field_search.tsx',
          name: 'EuiFieldSearchProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      compressed: {
        defaultValue: null,
        description: 'when `true` creates a shorter height input',
        name: 'compressed',
        parent: {
          fileName: 'app/src/components/form/field_search/field_search.tsx',
          name: 'EuiFieldSearchProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/field_search/field_search.tsx',
          name: 'EuiFieldSearchProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      inputRef: {
        defaultValue: null,
        description: '',
        name: 'inputRef',
        parent: {
          fileName: 'app/src/components/form/field_search/field_search.tsx',
          name: 'EuiFieldSearchProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/field_search/field_search.tsx',
          name: 'EuiFieldSearchProps'
        }],
        required: false,
        type: {
          name: '(node: HTMLInputElement) => void'
        }
      },
      isInvalid: {
        defaultValue: null,
        description: '',
        name: 'isInvalid',
        parent: {
          fileName: 'app/src/components/form/field_search/field_search.tsx',
          name: 'EuiFieldSearchProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/field_search/field_search.tsx',
          name: 'EuiFieldSearchProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isClearable: {
        defaultValue: null,
        description: 'Shows a button that quickly clears any input',
        name: 'isClearable',
        parent: {
          fileName: 'app/src/components/form/field_search/field_search.tsx',
          name: 'EuiFieldSearchProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/field_search/field_search.tsx',
          name: 'EuiFieldSearchProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      onChange: {
        defaultValue: null,
        description: 'Passes back (searchValue, matchingOptions)',
        name: 'onChange',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_search/selectable_search.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '(searchValue: string, matchingOptions: any[]) => void'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'EuiFieldSearchProps', 'InputHTMLAttributes', 'EuiSelectableTemplateSitewideMetaData']
  };
} catch (e) {}

try {
  Options.__docgenInfo = {
    tags: {},
    filePath: '/app/src-docs/src/views/selectable/props.tsx',
    description: '',
    displayName: 'Options',
    methods: [],
    props: {
      icon: {
        defaultValue: null,
        description: 'Displayed on the left (`prepend`).\n' + "Object of `EuiIconProps` for display of the solution/application's logo",
        name: 'icon',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_templates/selectable_template_sitewide_option.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'EuiIconProps'
        }
      },
      avatar: {
        defaultValue: null,
        description: 'Displayed on the right (`append`).\n' + 'Object of `EuiAvatarProps` for display of the space (default) or user',
        name: 'avatar',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_templates/selectable_template_sitewide_option.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'EuiAvatarProps'
        }
      },
      meta: {
        defaultValue: null,
        description: 'An array of inline #MetaData displayed beneath the label and separated by bullets.',
        name: 'meta',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_templates/selectable_template_sitewide_option.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'EuiSelectableTemplateSitewideMetaData[]'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      label: {
        defaultValue: null,
        description: 'Visible label of option.\n' + 'Must be unique across items if `key` is not supplied',
        name: 'label',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      searchableLabel: {
        defaultValue: null,
        description: 'Optionally change the searchable term by passing a different string other than the `label`.\n' + 'Best used when creating a custom `optionRender` to separate the label from metadata but allowing to search on both',
        name: 'searchableLabel',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      key: {
        defaultValue: null,
        description: 'Must be unique across items.\n' + 'Will be used to match options instead of `label`',
        name: 'key',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      checked: {
        defaultValue: null,
        description: 'Leave `undefined` to indicate not selected,\n' + "'on' to indicate inclusion and\n" + "'off' to indicate exclusion",
        name: 'checked',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'EuiSelectableOptionCheckedType',
          value: [{
            value: '"off"'
          }, {
            value: '"on"'
          }]
        }
      },
      disabled: {
        defaultValue: null,
        description: '',
        name: 'disabled',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isGroupLabel: {
        defaultValue: null,
        description: 'Optional `boolean`.\n' + 'Set to `true` to indicate object is just a grouping label, not a selectable item',
        name: 'isGroupLabel',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      prepend: {
        defaultValue: null,
        description: 'Node to add between the selection icon and the label',
        name: 'prepend',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'any'
        }
      },
      append: {
        defaultValue: null,
        description: 'Node to add to the far right of the item',
        name: 'append',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'any'
        }
      },
      ref: {
        defaultValue: null,
        description: '',
        name: 'ref',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '(optionIndex: number) => void'
        }
      },
      id: {
        defaultValue: null,
        description: 'Disallow `id` from being set.\n' + 'Option item `id`s are coordinated at a higher level for a11y reasons.',
        name: 'id',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'never'
        }
      },
      data: {
        defaultValue: null,
        description: 'Option data to pass through to the `renderOptions` element.\n' + 'Bypass `EuiSelectableItem` and avoid DOM attribute warnings.',
        name: 'data',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/selectable/selectable_option.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '{ [key: string]: any; }'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'EuiFieldSearchProps', 'InputHTMLAttributes', 'EuiSelectableTemplateSitewideMetaData']
  };
} catch (e) {}

try {
  MetaData.__docgenInfo = {
    tags: {},
    filePath: '/app/src-docs/src/views/selectable/props.tsx',
    description: '',
    displayName: 'MetaData',
    methods: [],
    props: {
      text: {
        defaultValue: null,
        description: 'Required to display the metadata',
        name: 'text',
        parent: {
          fileName: 'app/src/components/selectable/selectable_templates/selectable_template_sitewide_option.tsx',
          name: 'EuiSelectableTemplateSitewideMetaData'
        },
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_templates/selectable_template_sitewide_option.tsx',
          name: 'EuiSelectableTemplateSitewideMetaData'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      type: {
        defaultValue: null,
        description: "Styles the metadata according to Elastic's schema.\n" + "Can be one of 'application', 'deployment', 'article', 'case', 'platform',\n" + 'or a custom string to associate with your own schema.\n' + 'Appends the string to the class name as `euiSelectableTemplateSitewide__optionMeta--[type]`',
        name: 'type',
        parent: {
          fileName: 'app/src/components/selectable/selectable_templates/selectable_template_sitewide_option.tsx',
          name: 'EuiSelectableTemplateSitewideMetaData'
        },
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_templates/selectable_template_sitewide_option.tsx',
          name: 'EuiSelectableTemplateSitewideMetaData'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      highlightSearchString: {
        defaultValue: null,
        description: 'Will wrap the meta tag in EuiHighlight to mark the portions that match the search text',
        name: 'highlightSearchString',
        parent: {
          fileName: 'app/src/components/selectable/selectable_templates/selectable_template_sitewide_option.tsx',
          name: 'EuiSelectableTemplateSitewideMetaData'
        },
        declarations: [{
          fileName: 'app/src/components/selectable/selectable_templates/selectable_template_sitewide_option.tsx',
          name: 'EuiSelectableTemplateSitewideMetaData'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'EuiFieldSearchProps', 'InputHTMLAttributes', 'EuiSelectableTemplateSitewideMetaData']
  };
} catch (e) {}